import {useCallback, useEffect, useRef, useState} from 'react';
import {useRouter} from 'next/router';

import {fetchPlaylistsForUser} from '@modules/playlists/fetchPlaylistsForUser';
import {redirectToTmm} from '@modules/tmm/redirectToTmm';
import {startTmmLaunch} from '@modules/tmm/startTmmLaunch';
import {useAuth} from '@providers/AuthProvider';
import {useImportState} from '@providers/ImportStateProvider';

type LaunchStatus = 'idle' | 'loading' | 'redirecting' | 'error';

type UseTmmLaunchFlowInput = {
	enabled: boolean;
	locale: string;
};

type UseTmmLaunchFlowResult = {
	status: LaunchStatus;
	errorCode: string | null;
	retry: () => void;
};

type DeezerApiError = {
	error?: {
		type?: string;
		message?: string;
		code?: number | string;
	};
};

function getCacheKey(locale: string): string {
	return `spot-ai:tmm-launch:${locale}`;
}

function isPrivateAccountErrorPayload(value: unknown): boolean {
	if (!value || typeof value !== 'object') return false;

	const apiError = value as DeezerApiError;
	const message = apiError.error?.message;

	return (
		apiError.error?.type === 'OAuthException' &&
		typeof message === 'string' &&
		message.toLowerCase().includes("user's profile is private")
	);
}

function tryParseJson(value: string): unknown {
	try {
		return JSON.parse(value);
	} catch {
		return null;
	}
}

function isPrivateAccountError(err: unknown): boolean {
	if (isPrivateAccountErrorPayload(err)) {
		return true;
	}

	if (err instanceof Error) {
		const parsedMessage = tryParseJson(err.message);

		if (isPrivateAccountErrorPayload(parsedMessage)) {
			return true;
		}

		return err.message.toLowerCase().includes("user's profile is private");
	}

	return false;
}

function getErrorCode(err: unknown): string {
	if (isPrivateAccountError(err)) {
		return 'PRIVATE_ACCOUNT';
	}

	return err instanceof Error ? err.message : 'TMM_LAUNCH_FAILED';
}

function getErrorRoute(errorCode: string): string {
	if (errorCode === 'PRIVATE_ACCOUNT') {
		return '/error/private-account';
	}

	if (errorCode === 'REACH_LIMIT') {
		return '/error/reach-limits';
	}

	return '/error/something-went-wrong';
}

export function useTmmLaunchFlow({
	enabled,
	locale,
}: UseTmmLaunchFlowInput): UseTmmLaunchFlowResult {
	const router = useRouter();
	const [, {ensureSession}] = useAuth();
	const {setPlaylists} = useImportState();
	const [status, setStatus] = useState<LaunchStatus>('idle');
	const [errorCode, setErrorCode] = useState<string | null>(null);
	const startedRef = useRef(false);

	const launch = useCallback(async () => {
		if (startedRef.current) return;

		startedRef.current = true;
		setStatus('loading');
		setErrorCode(null);

		try {
			const session = await ensureSession();

			if (session.case === 'REAL_DEEZER') {
				const playlists = await fetchPlaylistsForUser();

				setPlaylists(playlists);
				setStatus('redirecting');

				void router.push('/analyze', undefined, {locale});
				return;
			}

			const cacheKey = getCacheKey(locale);
			const cachedRedirectUrl = window.sessionStorage.getItem(cacheKey);

			if (cachedRedirectUrl) {
				setStatus('redirecting');
				redirectToTmm(cachedRedirectUrl);
				return;
			}

			const redirectUrl = `${window.location.origin}/${locale}/import`;
			const tmmUrl = await startTmmLaunch({
				locale,
				redirectUrl,
			});

			window.sessionStorage.setItem(cacheKey, tmmUrl);
			setStatus('redirecting');
			redirectToTmm(tmmUrl);
		} catch (err) {
			const nextErrorCode = getErrorCode(err);

			startedRef.current = false;
			setStatus('error');
			setErrorCode(nextErrorCode);

			void router.replace(getErrorRoute(nextErrorCode), undefined, {locale});
		}
	}, [ensureSession, locale, router, setPlaylists]);

	useEffect(() => {
		if (!enabled) return;

		const timeoutId = window.setTimeout(() => {
			void launch();
		}, 0);

		return () => window.clearTimeout(timeoutId);
	}, [enabled, launch]);

	const retry = useCallback(() => {
		startedRef.current = false;
		void launch();
	}, [launch]);

	return {status, errorCode, retry};
}
